// src/locales/designer-i18n.js
//
// Translations for the new label designer (LabelDesignerView, panels, dialogs)
// and the logo library (LogoLibrary, LogoPicker). Merged into each locale's
// main file via spread:
//
//     import designerI18n from './designer-i18n.js'
//     export default { ...existingKeys, ...designerI18n.da }
//
// Native-speaker review needed for sv/no/fi/de/ja.
//
// Notes on structure:
// - `nutrition.*` and `logos.*` are top-level so renderers can read them
//   without nesting. They're not under `common` because they're feature-
//   scoped, not generic UI strings.
// - `nutritionProfiles` and `logoLibrary` are flat top-level keys because
//   that's what ConfigurationsView's <v-tab> labels use: t('nutritionProfiles')
//   not t('common.nutritionProfiles').
// - `element.nutrition*` keys live under `element` because they belong to
//   the properties panel — same convention as element.dateSection,
//   element.databaseSection, etc.
//
// PHASE 3 OF DATE UNIFICATION (2026-06-13):
// New keys added under element.* for the unified date model:
//   - showLabelOn / showLabelOff / labelTextHint        (showLabel toggle)
//   - dateMode + dateModePrint/Produced/Expiry/Fixed    (mode picker)
//   - daysOffsetFixed + daysOffsetFixedOption           (expiry fixed days)
//   - fixedDate                                         (calendar picker)

const designerI18n = {
  // ─────────────────────────────────────────────────────────
  // Danish (da)
  // ─────────────────────────────────────────────────────────
  da: {
    // Top-level tab labels used by ConfigurationsView's flat t() calls.
    logoLibrary:       "Logo-bibliotek",
    nutritionProfiles: "Næringsprofiler",

    designer: {
      untitled:        "Uden titel",
      noLabel:         "Ingen etiket valgt",
      saved:           "Gemt",
      saveError:       "Kunne ikke gemme",
      unsavedTitle:    "Ugemte ændringer",
      unsavedConfirm:  "Du har ugemte ændringer. Vil du forlade siden?",
      leaveAnyway:     "Forlad alligevel",
      stay:            "Bliv",
      toggleGrid:      "Skift gitter",
      preview:         "Forhåndsvisning",
      undo:            "Fortryd",
      redo:            "Gentag",
      save:            "Gem",
      print:           "Udskriv",
      back:            "Tilbage",
      deleteElement:   "Slet element",
    },
    fields: {
      productFields:  "Produktfelter",
      productField:   "Produktfelt",
      dateLogic:      "Dato & logik",
      date:           "Dato",
      datePlus:       "Dato + dage",
      valueOnly:      "Kun værdi",
      prompt:         "Indtastnings prompt",
      barcodes:       "Stregkoder",
      barcode:        "Stregkode / QR",
      layout:         "Layout",
      staticText:     "Statisk tekst",
      allergens:      "Allergener",
      nutrition:      "Næringsindhold",
      imageLogo:      "Logo billede",
      divider:        "Skillelinje",
      productName:    "Produktnavn",
      tokens: {
        product_number: "Produktnummer",
        product_name:   "Produktnavn",
        description:    "Beskrivelse",
        three_hours:    "3-hour rule",
        fridge:         "Storage",
      },
    },
    label: {
      name:    "Etiketnavn",
      width:   "Bredde (mm)",
      height:  "Højde (mm)",
      unit:    "Enhed",
      mm:      "mm",
      inch:    "tomme",
      colour:  "Farve",
    },
    canvas: {
      grid:          "Vis gitter",
      gridSize:      "Gitterstørrelse (mm)",
      layers:        "Lag",
      offCanvas:     "Uden for etiket",
      resetPosition: "Flyt tilbage til 0,0",
      locked:        "Låst",
      unlocked:      "Ulåst",
      layer:         "Lag",
      bringFront:    "Forrest",
      sendBack:      "Bagest",
      bringForward:  "Et lag frem",
      sendBackward:  "Et lag tilbage",
      lockPosition:  "Lås position",
      unlockPosition:"Lås op",
    },
    element: {
      title:                   "Element",
      position:                "Position",
      size:                    "Størrelse",
      rotation:                "Rotation",
      typography:              "Typografi",
      labelText:               "Etikettekst",
      labelTextHint:           "Vises foran værdien",
      showLabelOn:             "Vis prefiks",
      showLabelOff:            "Skjul prefiks",
      showLabelToggle:         "Vis prefiks",
      font:                    "Skrifttype",
      fontDefault:             "Standard",
      fontSize:                "Skriftstørrelse",
      align:                   "Justering",
      left:                    "Venstre",
      center:                  "Centreret",
      right:                   "Højre",
      bold:                    "Fed",
      italic:                  "Kursiv",
      wordWrap:                "Ombryd tekst",
      maxLines:                "Max linjer",
      wrapWidthHint:           "Teksten ombrydes inden for elementets bredde (W ovenfor)",
      fontBuiltIn:             "standard",
      dateFormat:              "Datoformat",
      dateFormatAuto:          "Følg sprog",
      staticSection:           "Statisk tekst",
      staticText:              "Tekst",
      staticTextHint:          "Teksten der printes på etiketten",
      dateSection:             "Dato-indstillinger",
      dateMode:                "Type",
      dateModePrint:           "Print-dato",
      dateModeProduced:        "Produktionsdato",
      dateModeExpiry:          "Udløbsdato",
      dateModeFixed:           "Specifik dato",
      dateBase:                "Kilde",
      producedDate:            "Produktionsdato",
      openedDate:              "Åbningsdato",
      daysOffsetField:         "Dage fra felt",
      daysOffsetFixed:         "Antal dage",
      daysOffsetFixedOption:   "Fast antal dage",
      fixedDate:               "Vælg dato",
      thawingDays:             "Optøningsdage",
      refrigeratedDays:        "Køledage",
      expiryDays:              "Holdbarhedsdage",
      showTime:                "Vis klokkeslæt",
      databaseSection:         "Database-felt",
      databaseField:           "Felt",
      dbProductName:           "Produktnavn",
      dbProductNumber:         "Produktnummer",
      dbDescription:           "Beskrivelse",
      dbEanCode:               "EAN / Stregkode",
      dbAllergens:             "Allergener",
      dbNotes:                 "Noter",
      dbFridge:                "Køleskabsværdi",
      dbThreeHours:            "Tre timer (JA/NEJ)",
      valueOnlySection:        "Værdikilde",
      valueOnlyType:           "Kildetype",
      valueTypeDate:           "Dato",
      valueTypeDatePlus:       "Dato + offset",
      valueTypeDatabase:       "Database",
      descriptionSection:      "Beskrivelse",
      maxWidth:                "Max bredde (mm)",
      descriptionMaxWidthHint: "Teksten ombrydes inden for denne bredde",
      allergensSection:        "Allergener",
      allergenSeparator:       "Separator",
      allergenSeparatorHint:   'f.eks. ", " eller " · "',
      allergenCase:            "Versaler",
      allergenCaseHint:        "Styrer hvordan allergener vises på etiketten",
      allergenCaseKeep:        "Som indtastet",
      allergenCaseLower:       "små bogstaver",
      allergenCaseUpper:       "STORE BOGSTAVER",
      allergenCaseTitle:       "Første Bogstav Stort",
      valuePosition:           "Værdiplacering",
      separator:               "Separator",
      separatorHint:           "Tekst mellem label og værdi. Lad stå tom for ingen separator.",
      valueX:                  "Værdi X (mm)",
      valueY:                  "Værdi Y (mm)",
      valueFontSize:           "Værdi-skriftstørrelse",
      barcode:                 "Stregkode",
      barcodeType:             "Stregkode-type",
      dataField:               "Datafelt",
      customData:              "Brugerdefinerede data",
      customDataValue:         "Brugerdefineret værdi",
      customDataHint:          "Brug {product_number}, {product_name} som pladsholdere",
      showValue:               "Vis værdi",
      qrCode:                  "QR-kode",
      qrSize:                  "Størrelse (mm)",
      qrSquareHint:            "QR-koder er kvadratiske — én størrelse sætter begge dimensioner.",
      customUrl:               "Brugerdefineret URL / data",
      customUrlHint:           "Brug {product_number}, {product_name} som pladsholdere",
      image:                   "Billede",
      replaceImage:            "Skift billede",
      uploadImage:             "Upload billede",
      logoBound:               "Tilknyttet logo",
      chooseImage:             "Vælg logo",
      changeImage:             "Skift logo",
      divider:                 "Skillelinje",
      lineWidth:               "Linjebredde",
      lineThickness:           "Linjetykkelse",
      nutritionSection:        "Næringsindhold",
      nutritionShowHeader:     "Vis overskrift (Næringsindhold:)",
      nutritionShowReference:  "Vis reference (per 100g)",
      nutritionSubIndent:      "Indrykningstegn for underrækker",
      nutritionSubIndentHint:  "Vises foran -heraf rækker",
      nutritionRows:           "Synlige rækker",
      nutritionDecimals:       "Antal decimaler",
      nutritionDecimalsHint:   "Antal cifre efter komma (0-3)",
      nutritionFontWarning:    "Skriftstørrelse under 2,4mm kan være under EU-minimum for x-højde (1169/2011).",
      promptSection:           "Input prompt",
      promptToken:             "Input tekst",
      promptTokenHint:         "Vises i forhåndsvisning og på etiketten foran et inputfelt",
    },
    nutrition: {
      title:        "Næringsindhold",
      per_100g:     "per 100g",
      per_100ml:    "per 100ml",
      per_portion:  "per portion",
      energy:       "Energi",
      fat:          "Fedt",
      saturated:    "heraf mættede fedtsyrer",
      carbs:        "Kulhydrat",
      sugars:       "heraf sukkerarter",
      protein:      "Protein",
      salt:         "Salt",
      profiles:           "Næringsprofiler",
      profilesHint:       "Opret genanvendelige næringsindholdsprofiler der kan kobles til produkter og vises på etiketter.",
      noProfiles:         "Ingen næringsprofiler oprettet endnu.",
      newProfile:         "Ny profil",
      editProfile:        "Rediger profil",
      profileName:        "Navn",
      reference:          "Reference",
      portionSize:        "Portionsstørrelse (fx 160g)",
      values:             "Næringsværdier",
      required:           "Påkrævet",
      confirmDelete:      "Bekræft sletning",
      confirmDeleteText:  "Slet profilen",
      deleteWarning:      "Produkter der bruger denne profil mister deres næringsindhold.",
      errorSaving:        "Kunne ikke gemme profil",
      errorLoading:       "Kunne ikke hente profiler",
    },
    logos: {
      libraryTitle:           "Logo-bibliotek",
      libraryHint:            "Logoer der kan tilføjes til etiketter i designeren. Uploadede logoer kan genbruges på flere etiketter.",
      uploadButton:           "Upload logo",
      noLogos:                "Ingen logoer endnu. Upload et logo for at komme i gang.",
      preview:                "Forhåndsvisning",
      name:                   "Navn",
      dimensions:             "Mål",
      usedBy:                 "Bruges af",
      uploaded:               "Uploadet",
      confirmDeleteLogo:      "Er du sikker på at du vil slette dette logo?",
      deleteLogoInUseWarning: "Dette logo er i brug. Logo-elementerne forbliver på etiketterne, men vises tomme indtil et nyt logo tilknyttes.",
      confirmBulkDelete:      "Bekræft masseslettning",
      confirmBulkDeleteText:  "Er du sikker på at du vil slette",
      logosWord:              "logo(er)",
      selectedInUseWarning:   "af de valgte logoer er i brug. Logo-elementerne forbliver på etiketterne, men vises tomme indtil et nyt logo tilknyttes.",
      andMore:                "og",
      more:                   "flere",
      deleteSelected:         "Slet valgte",
      deleteAll:              "Slet alle",
      logosDeleted:           "Slettet",
      logoDeleted:            "Logo slettet",
      logoDeletedInUse:       "Logo slettet (etiketter beholdt med tomme logo-pladser)",
      logoRenamed:            "Logo omdøbt",
      logoUploaded:           "Logo uploadet",
      uploadFailed:           "Upload mislykkedes",
      deleteFailed:           "Sletning mislykkedes",
      bulkDeleteFailed:       "Masseslettning mislykkedes",
      renameFailed:           "Omdøbning mislykkedes",
      missing:                "manglede",
      skipped:                "sprunget over",
    },
    freetext: {
      sizes:      "Fritekst-størrelser",
      sizesHint:  "Etiketstørrelser brugerne kan vælge når de printer fritekst fra tablet'en. Markér én som standard.",
      createSize: "Opret størrelse",
    },
    dialogs: {
      preview: {
        title:             "Forhåndsvisning",
        hint:              "Udfyld værdier for at se hvordan etiketten ser ud med rigtige data. Brug 'Hent prøvedata' for at hente et tilfældigt produkt.",
        loadSample:        "Hent prøvedata",
        sampleLoaded:      "Prøvedata indlæst ({name})",
        sampleFailed:      "Kunne ikke hente prøvedata",
        noSampleAvailable: "Ingen aktive produkter at vælge prøvedata fra",
        clearForm:         "Ryd form",
        apply:             "Anvend",
        noTokens:          "Ingen tokens på denne etiket endnu",
        pickDate:          "Vælg dato",
        tabDate:           "Dato",
        tabTime:           "Tid",
        now:               "Nu",
        pickProduct:       "Vælg produkt",
        searchProduct:     "Søg…",
        noProducts:        "Ingen aktive produkter",
        itemNotFound:      "Produkt findes ikke",
        productListFailed: "Kunne ikke hente produktliste",
      },
      print: {
        title:        "Udskriv etiket",
        hint:         "Hent en PDF eller ZPL af etiketten.",
        format:       "Format",
        dpi:          "Opløsning",
        noDataHint:   "Ingen prøvedata indlæst. Etiketten vises med pladsholdere. Åbn Forhåndsvisning for at indlæse rigtige værdier.",
        generateZpl:  "Generér ZPL",
        zplOutput:    "ZPL output",
        copyZpl:      "Kopiér ZPL",
        downloadZpl:  "Hent .zpl",
        copied:       "Kopieret til udklipsholder",
        copyFailed:   "Kunne ikke kopiere",
        pdfSuccess:   "PDF hentet",
        failed:       "Kunne ikke generere",
      },
      logo: {
        title:             "Upload logo",
        selectFile:        "Vælg billede",
        fileHint:          "PNG, JPG, GIF eller WebP. Max 5 MB.",
        widthMm:           "Bredde (mm)",
        widthHint:         "Højden beregnes automatisk",
        dpi:               "Opløsning",
        threshold:         "Sort/hvid tærskel",
        thresholdHint:     "Lavere = mere hvidt. Højere = mere sort.",
        original:          "Original",
        mono:              "Sort/hvid",
        apply:             "Anvend",
      },
      logoPicker: {
        title:             "Vælg logo",
        uploadNew:         "Upload nyt",
        search:            "Søg logo...",
        empty:             "Ingen logoer endnu. Upload dit første logo for at komme i gang.",
        noMatch:           "Ingen logoer matcher søgningen",
      },
    },
    common: {
      unsaved:  "Ugemte ændringer",
      cancel:   "Annullér",
      close:    "Luk",
      download: "Hent",
      clear:    "Ryd",
      refresh:  "Opdater",
      delete:   "Slet",
      loading:  "Indlæser…",
      nutritionProfile:  "Næringsprofil",
      nutritionProfiles: "Næringsprofiler",
    },
  },

  // ─────────────────────────────────────────────────────────
  // English (en)
  // ─────────────────────────────────────────────────────────
  en: {
    logoLibrary:       "Logo library",
    nutritionProfiles: "Nutrition profiles",

    designer: {
      untitled:        "Untitled",
      noLabel:         "No label selected",
      saved:           "Saved",
      saveError:       "Could not save",
      unsavedTitle:    "Unsaved changes",
      unsavedConfirm:  "You have unsaved changes. Do you want to leave the page?",
      leaveAnyway:     "Leave anyway",
      stay:            "Stay",
      toggleGrid:      "Toggle grid",
      preview:         "Preview",
      undo:            "Undo",
      redo:            "Redo",
      save:            "Save",
      print:           "Print",
      back:            "Back",
      deleteElement:   "Delete element",
    },
    fields: {
      productFields:  "Product fields",
      productField:   "Product field",
      dateLogic:      "Date & logic",
      date:           "Date",
      datePlus:       "Date + days",
      valueOnly:      "Value only",
      prompt:         "Input prompt",
      barcodes:       "Barcodes",
      barcode:        "Barcode / QR",
      qrCode:         "QR code",
      layout:         "Layout",
      staticText:     "Static text",
      allergens:      "Allergens",
      nutrition:      "Nutrition",
      imageLogo:      "Logo image",
      divider:        "Divider",
      productName:    "Product name",
      tokens: {
        product_number: "Product number",
        product_name:   "Product name",
        description:    "Description",
        three_hours:    "3-hour rule",
        fridge:         "Storage",
      },
    },
    label: {
      name:    "Label name",
      width:   "Width (mm)",
      height:  "Height (mm)",
      unit:    "Unit",
      mm:      "mm",
      inch:    "inch",
      colour:  "Colour",
    },
    canvas: {
      grid:          "Show grid",
      gridSize:      "Grid size (mm)",
      layers:        "Layers",
      offCanvas:     "Off-canvas",
      resetPosition: "Move back to 0,0",
      locked:        "Locked",
      unlocked:      "Unlocked",
      layer:         "Layer",
      bringFront:    "Bring to front",
      sendBack:      "Send to back",
      bringForward:  "Bring forward",
      sendBackward:  "Send backward",
      lockPosition:  "Lock position",
      unlockPosition:"Unlock",
    },
    element: {
      title:                   "Element",
      position:                "Position",
      size:                    "Size",
      rotation:                "Rotation",
      typography:              "Typography",
      labelText:               "Label text",
      labelTextHint:           "Shown in front of the value",
      showLabelOn:             "Show label",
      showLabelOff:            "Hide label",
      showLabelToggle:         "Show label",
      font:                    "Font",
      fontDefault:             "Default",
      fontSize:                "Font size",
      align:                   "Align",
      left:                    "Left",
      center:                  "Center",
      right:                   "Right",
      bold:                    "Bold",
      italic:                  "Italic",
      wordWrap:                "Word wrap",
      maxLines:                "Max lines",
      wrapWidthHint:           "Text wraps within the element's width (W above)",
      fontBuiltIn:             "default",
      dateFormat:              "Date format",
      dateFormatAuto:          "Follow language",
      staticSection:           "Static text",
      staticText:              "Text",
      staticTextHint:          "The text printed on the label",
      dateSection:             "Date settings",
      dateMode:                "Type",
      dateModePrint:           "Print date",
      dateModeProduced:        "Produced date",
      dateModeExpiry:          "Expiry date",
      dateModeFixed:           "Specific date",
      dateBase:                "Source",
      producedDate:            "Produced date",
      openedDate:              "Opened date",
      daysOffsetField:         "Days from field",
      daysOffsetFixed:         "Number of days",
      daysOffsetFixedOption:   "Fixed number of days",
      fixedDate:               "Pick date",
      thawingDays:             "Thawing days",
      refrigeratedDays:        "Refrigerated days",
      expiryDays:              "Shelf-life days",
      showTime:                "Show time",
      databaseSection:         "Database field",
      databaseField:           "Field",
      dbProductName:           "Product name",
      dbProductNumber:         "Product number",
      dbDescription:           "Description",
      dbEanCode:               "EAN / Barcode",
      dbAllergens:             "Allergens",
      dbNotes:                 "Notes",
      dbFridge:                "Fridge value",
      dbThreeHours:            "Three hours (Yes/No)",
      valueOnlySection:        "Value source",
      valueOnlyType:           "Source type",
      valueTypeDate:           "Date",
      valueTypeDatePlus:       "Date + offset",
      valueTypeDatabase:       "Database",
      descriptionSection:      "Description",
      maxWidth:                "Max width (mm)",
      descriptionMaxWidthHint: "Text wraps within this width",
      allergensSection:        "Allergens",
      allergenSeparator:       "Separator",
      allergenSeparatorHint:   'e.g. ", " or " · "',
      allergenCase:            "Letter case",
      allergenCaseHint:        "Controls how allergens display on the label",
      allergenCaseKeep:        "As entered",
      allergenCaseLower:       "lower case",
      allergenCaseUpper:       "UPPER CASE",
      allergenCaseTitle:       "Title Case",
      valuePosition:           "Value position",
      separator:               "Separator",
      separatorHint:           "Text between label and value. Leave empty for no separator.",
      valueX:                  "Value X (mm)",
      valueY:                  "Value Y (mm)",
      valueFontSize:           "Value font size",
      barcode:                 "Barcode",
      barcodeType:             "Barcode type",
      dataField:               "Data field",
      customData:              "Custom data",
      customDataValue:         "Custom value",
      customDataHint:          "Use {product_number}, {product_name} as placeholders",
      showValue:               "Show value",
      qrCode:                  "QR code",
      qrSize:                  "Size (mm)",
      qrSquareHint:            "QR codes are square — one size sets both dimensions.",
      customUrl:               "Custom URL / data",
      customUrlHint:           "Use {product_number}, {product_name} as placeholders",
      image:                   "Image",
      replaceImage:            "Replace image",
      uploadImage:             "Upload image",
      logoBound:               "Bound logo",
      chooseImage:             "Choose logo",
      changeImage:             "Change logo",
      divider:                 "Divider",
      lineWidth:               "Line width",
      lineThickness:           "Line thickness",
      nutritionSection:        "Nutrition",
      nutritionShowHeader:     "Show header (Nutrition:)",
      nutritionShowReference:  "Show reference (per 100g)",
      nutritionSubIndent:      "Sub-row indent character",
      nutritionSubIndentHint:  "Shown before of-which rows",
      nutritionRows:           "Visible rows",
      nutritionDecimals:       "Decimals",
      nutritionDecimalsHint:   "Number of digits after the separator (0-3)",
      nutritionFontWarning:    "Font size below 2.4mm may be under the EU minimum x-height (1169/2011).",
      promptSection:           "Input prompt",
      promptToken:             "Input text",
      promptTokenHint:         "Shown in preview and on the label in front of an input field",
    },
    nutrition: {
      title:        "Nutrition",
      per_100g:     "per 100g",
      per_100ml:    "per 100ml",
      per_portion:  "per portion",
      energy:       "Energy",
      fat:          "Fat",
      saturated:    "of which saturates",
      carbs:        "Carbohydrate",
      sugars:       "of which sugars",
      protein:      "Protein",
      salt:         "Salt",
      profiles:           "Nutrition profiles",
      profilesHint:       "Create reusable nutrition profiles that can be linked to products and shown on labels.",
      noProfiles:         "No nutrition profiles created yet.",
      newProfile:         "New profile",
      editProfile:        "Edit profile",
      profileName:        "Name",
      reference:          "Reference",
      portionSize:        "Portion size (e.g. 160g)",
      values:             "Nutrition values",
      required:           "Required",
      confirmDelete:      "Confirm delete",
      confirmDeleteText:  "Delete profile",
      deleteWarning:      "Products using this profile will lose their nutrition data.",
      errorSaving:        "Could not save profile",
      errorLoading:       "Could not load profiles",
    },
    logos: {
      libraryTitle:           "Logo library",
      libraryHint:            "Logos that can be added to labels in the designer. Uploaded logos can be reused on multiple labels.",
      uploadButton:           "Upload logo",
      noLogos:                "No logos yet. Upload one to get started.",
      preview:                "Preview",
      name:                   "Name",
      dimensions:             "Size",
      usedBy:                 "Used by",
      uploaded:               "Uploaded",
      confirmDeleteLogo:      "Are you sure you want to delete this logo?",
      deleteLogoInUseWarning: "This logo is in use. Logo elements stay on the labels but show empty until a new logo is bound.",
      confirmBulkDelete:      "Confirm bulk delete",
      confirmBulkDeleteText:  "Are you sure you want to delete",
      logosWord:              "logo(s)",
      selectedInUseWarning:   "of the selected logos are in use. Logo elements stay on the labels but show empty until a new logo is bound.",
      andMore:                "and",
      more:                   "more",
      deleteSelected:         "Delete selected",
      deleteAll:              "Delete all",
      logosDeleted:           "Deleted",
      logoDeleted:            "Logo deleted",
      logoDeletedInUse:       "Logo deleted (labels kept with empty logo slots)",
      logoRenamed:            "Logo renamed",
      logoUploaded:           "Logo uploaded",
      uploadFailed:           "Upload failed",
      deleteFailed:           "Delete failed",
      bulkDeleteFailed:       "Bulk delete failed",
      renameFailed:           "Rename failed",
      missing:                "missing",
      skipped:                "skipped",
    },
    freetext: {
      sizes:      "Freetext sizes",
      sizesHint:  "Label sizes users can choose when printing freetext from the tablet. Mark one as default.",
      createSize: "Create size",
    },
    dialogs: {
      preview: {
        title:             "Preview",
        hint:              "Fill in values to see how the label looks with real data. Use 'Load sample data' to fetch a random product.",
        loadSample:        "Load sample data",
        sampleLoaded:      "Sample loaded ({name})",
        sampleFailed:      "Could not load sample data",
        noSampleAvailable: "No active products to sample from",
        clearForm:         "Clear form",
        apply:             "Apply",
        noTokens:          "No tokens on this label yet",
        pickDate:          "Pick a date",
        tabDate:           "Date",
        tabTime:           "Time",
        now:               "Now",
        pickProduct:       "Pick product",
        searchProduct:     "Search…",
        noProducts:        "No active products",
        itemNotFound:      "Product not found",
        productListFailed: "Could not load product list",
      },
      print: {
        title:        "Print label",
        hint:         "Download the label as a PDF or ZPL.",
        format:       "Format",
        dpi:          "Resolution",
        noDataHint:   "No sample data loaded. The label will render with placeholders. Open Preview to load real values.",
        generateZpl:  "Generate ZPL",
        zplOutput:    "ZPL output",
        copyZpl:      "Copy ZPL",
        downloadZpl:  "Download .zpl",
        copied:       "Copied to clipboard",
        copyFailed:   "Could not copy",
        pdfSuccess:   "PDF downloaded",
        failed:       "Could not generate",
      },
      logo: {
        title:             "Upload logo",
        selectFile:        "Choose image",
        fileHint:          "PNG, JPG, GIF or WebP. Max 5 MB.",
        widthMm:           "Width (mm)",
        widthHint:         "Height is computed automatically",
        dpi:               "Resolution",
        threshold:         "Black/white threshold",
        thresholdHint:     "Lower = more white. Higher = more black.",
        original:          "Original",
        mono:              "Mono",
        apply:             "Apply",
      },
      logoPicker: {
        title:             "Choose logo",
        uploadNew:         "Upload new",
        search:            "Search logo...",
        empty:             "No logos yet. Upload your first to get started.",
        noMatch:           "No logos match your search",
      },
    },
    common: {
      unsaved:  "Unsaved changes",
      cancel:   "Cancel",
      close:    "Close",
      download: "Download",
      clear:    "Clear",
      refresh:  "Refresh",
      delete:   "Delete",
      loading:  "Loading…",
      nutritionProfile:  "Nutrition profile",
      nutritionProfiles: "Nutrition profiles",
    },
  },

  // ─────────────────────────────────────────────────────────
  // Swedish (sv)
  // ─────────────────────────────────────────────────────────
  sv: {
    logoLibrary:       "Logobibliotek",
    nutritionProfiles: "Näringsprofiler",

    designer: {
      untitled:        "Namnlös",
      noLabel:         "Ingen etikett vald",
      saved:           "Sparad",
      saveError:       "Kunde inte spara",
      unsavedTitle:    "Osparade ändringar",
      unsavedConfirm:  "Du har osparade ändringar. Vill du lämna sidan?",
      leaveAnyway:     "Lämna ändå",
      stay:            "Stanna",
      toggleGrid:      "Växla rutnät",
      preview:         "Förhandsvisning",
      undo:            "Ångra",
      redo:            "Gör om",
      save:            "Spara",
      print:           "Skriv ut",
      back:            "Tillbaka",
      deleteElement:   "Ta bort element",
    },
    fields: {
      productFields:  "Produktfält",
      productField:   "Produktfält",
      dateLogic:      "Datum & logik",
      date:           "Datum",
      datePlus:       "Datum + dagar",
      valueOnly:      "Endast värde",
      prompt:         "Inmatningsfråga",
      barcodes:       "Streckkoder",
      barcode:        "Streckkod / QR",
      qrCode:         "QR-kod",
      layout:         "Layout",
      staticText:     "Statisk text",
      allergens:      "Allergener",
      nutrition:      "Näringsinnehåll",
      imageLogo:      "Logobild",
      divider:        "Avdelare",
      productName:    "Produktnamn",
      tokens: {
        product_number: "Produktnummer",
        product_name:   "Produktnamn",
        description:    "Beskrivning",
        three_hours:    "3-timmarsregel",
        fridge:         "Förvaring",
      },
    },
    label: {
      name:    "Etikettnamn",
      width:   "Bredd (mm)",
      height:  "Höjd (mm)",
      unit:    "Enhet",
      mm:      "mm",
      inch:    "tum",
      colour:  "Färg",
    },
    canvas: {
      grid:          "Visa rutnät",
      gridSize:      "Rutnätsstorlek (mm)",
      layers:        "Lager",
      offCanvas:     "Utanför etiketten",
      resetPosition: "Flytta tillbaka till 0,0",
      locked:        "Låst",
      unlocked:      "Olåst",
      layer:         "Lager",
      bringFront:    "Längst fram",
      sendBack:      "Längst bak",
      bringForward:  "Ett lager framåt",
      sendBackward:  "Ett lager bakåt",
      lockPosition:  "Lås position",
      unlockPosition:"Lås upp",
    },
    element: {
      title:                   "Element",
      position:                "Position",
      size:                    "Storlek",
      rotation:                "Rotation",
      typography:              "Typografi",
      labelText:               "Etiketttext",
      labelTextHint:           "Visas framför värdet",
      showLabelOn:             "Visa etikett",
      showLabelOff:            "Dölj etikett",
      showLabelToggle:         "Visa etikett",
      font:                    "Typsnitt",
      fontDefault:             "Standard",
      fontSize:                "Teckenstorlek",
      align:                   "Justering",
      left:                    "Vänster",
      center:                  "Centrerad",
      right:                   "Höger",
      bold:                    "Fet",
      italic:                  "Kursiv",
      wordWrap:                "Radbryt text",
      maxLines:                "Max rader",
      wrapWidthHint:           "Texten radbryts inom elementets bredd (W ovan)",
      fontBuiltIn:             "standard",
      dateFormat:              "Datumformat",
      dateFormatAuto:          "Följ språk",
      staticSection:           "Statisk text",
      staticText:              "Text",
      staticTextHint:          "Texten som skrivs ut på etiketten",
      dateSection:             "Datum-inställningar",
      dateMode:                "Typ",
      dateModePrint:           "Utskriftsdatum",
      dateModeProduced:        "Produktionsdatum",
      dateModeExpiry:          "Utgångsdatum",
      dateModeFixed:           "Specifikt datum",
      dateBase:                "Källa",
      producedDate:            "Produktionsdatum",
      openedDate:              "Öppningsdatum",
      daysOffsetField:         "Dagar från fält",
      daysOffsetFixed:         "Antal dagar",
      daysOffsetFixedOption:   "Fast antal dagar",
      fixedDate:               "Välj datum",
      thawingDays:             "Upptiningsdagar",
      refrigeratedDays:        "Kyldagar",
      expiryDays:              "Hållbarhetsdagar",
      showTime:                "Visa tid",
      databaseSection:         "Databas-fält",
      databaseField:           "Fält",
      dbProductName:           "Produktnamn",
      dbProductNumber:         "Produktnummer",
      dbDescription:           "Beskrivning",
      dbEanCode:               "EAN / Streckkod",
      dbAllergens:             "Allergener",
      dbNotes:                 "Anteckningar",
      dbFridge:                "Kylskåpsvärde",
      dbThreeHours:            "Tre timmar (Ja/Nej)",
      valueOnlySection:        "Värdekälla",
      valueOnlyType:           "Källtyp",
      valueTypeDate:           "Datum",
      valueTypeDatePlus:       "Datum + offset",
      valueTypeDatabase:       "Databas",
      descriptionSection:      "Beskrivning",
      maxWidth:                "Max bredd (mm)",
      descriptionMaxWidthHint: "Texten radbryts inom denna bredd",
      allergensSection:        "Allergener",
      allergenSeparator:       "Separator",
      allergenSeparatorHint:   't.ex. ", " eller " · "',
      allergenCase:            "Versaler",
      allergenCaseHint:        "Styr hur allergener visas på etiketten",
      allergenCaseKeep:        "Som angivet",
      allergenCaseLower:       "små bokstäver",
      allergenCaseUpper:       "VERSALER",
      allergenCaseTitle:       "Inledande Versal",
      valuePosition:           "Värdeplacering",
      separator:               "Separator",
      separatorHint:           "Text mellan etikett och värde. Lämna tomt för ingen separator.",
      valueX:                  "Värde X (mm)",
      valueY:                  "Värde Y (mm)",
      valueFontSize:           "Värde-teckenstorlek",
      barcode:                 "Streckkod",
      barcodeType:             "Streckkod-typ",
      dataField:               "Datafält",
      customData:              "Anpassad data",
      customDataValue:         "Anpassat värde",
      customDataHint:          "Använd {product_number}, {product_name} som platshållare",
      showValue:               "Visa värde",
      qrCode:                  "QR-kod",
      qrSize:                  "Storlek (mm)",
      qrSquareHint:            "QR-koder är kvadratiska — en storlek anger båda måtten.",
      customUrl:               "Anpassad URL / data",
      customUrlHint:           "Använd {product_number}, {product_name} som platshållare",
      image:                   "Bild",
      replaceImage:            "Byt bild",
      uploadImage:             "Ladda upp bild",
      logoBound:               "Bunden logo",
      chooseImage:             "Välj logo",
      changeImage:             "Byt logo",
      divider:                 "Avdelare",
      lineWidth:               "Linjebredd",
      lineThickness:           "Linjetjocklek",
      nutritionSection:        "Näringsinnehåll",
      nutritionShowHeader:     "Visa rubrik (Näringsinnehåll:)",
      nutritionShowReference:  "Visa referens (per 100g)",
      nutritionSubIndent:      "Indragstecken för underrader",
      nutritionSubIndentHint:  "Visas före varav-rader",
      nutritionRows:           "Synliga rader",
      nutritionDecimals:       "Antal decimaler",
      nutritionDecimalsHint:   "Antal siffror efter kommatecknet (0-3)",
      nutritionFontWarning:    "Teckenstorlek under 2,4mm kan vara under EU-minimum för x-höjd (1169/2011).",
      promptSection:           "Inmatningsfråga",
      promptToken:             "Inmatningstext",
      promptTokenHint:         "Visas i förhandsvisning och på etiketten framför ett inmatningsfält",
    },
    nutrition: {
      title:        "Näringsinnehåll",
      per_100g:     "per 100g",
      per_100ml:    "per 100ml",
      per_portion:  "per portion",
      energy:       "Energi",
      fat:          "Fett",
      saturated:    "varav mättat fett",
      carbs:        "Kolhydrat",
      sugars:       "varav sockerarter",
      protein:      "Protein",
      salt:         "Salt",
      profiles:           "Näringsprofiler",
      profilesHint:       "Skapa återanvändbara näringsprofiler som kan kopplas till produkter och visas på etiketter.",
      noProfiles:         "Inga näringsprofiler skapade ännu.",
      newProfile:         "Ny profil",
      editProfile:        "Redigera profil",
      profileName:        "Namn",
      reference:          "Referens",
      portionSize:        "Portionsstorlek (t.ex. 160g)",
      values:             "Näringsvärden",
      required:           "Obligatoriskt",
      confirmDelete:      "Bekräfta borttagning",
      confirmDeleteText:  "Ta bort profil",
      deleteWarning:      "Produkter som använder denna profil förlorar sina näringsdata.",
      errorSaving:        "Kunde inte spara profilen",
      errorLoading:       "Kunde inte ladda profiler",
    },
    logos: {
      libraryTitle:           "Logobibliotek",
      libraryHint:            "Logotyper som kan läggas till på etiketter i designern. Uppladdade logotyper kan återanvändas på flera etiketter.",
      uploadButton:           "Ladda upp logo",
      noLogos:                "Inga logotyper ännu. Ladda upp en för att komma igång.",
      preview:                "Förhandsvisning",
      name:                   "Namn",
      dimensions:             "Storlek",
      usedBy:                 "Används av",
      uploaded:               "Uppladdad",
      confirmDeleteLogo:      "Är du säker på att du vill ta bort denna logo?",
      deleteLogoInUseWarning: "Denna logo används. Logo-elementen blir kvar på etiketterna men visas tomma tills en ny logo binds.",
      confirmBulkDelete:      "Bekräfta massborttagning",
      confirmBulkDeleteText:  "Är du säker på att du vill ta bort",
      logosWord:              "logo(typ)er",
      selectedInUseWarning:   "av de valda logotyperna används. Logo-elementen blir kvar på etiketterna men visas tomma tills en ny logo binds.",
      andMore:                "och",
      more:                   "fler",
      deleteSelected:         "Ta bort valda",
      deleteAll:              "Ta bort alla",
      logosDeleted:           "Borttagna",
      logoDeleted:            "Logo borttagen",
      logoDeletedInUse:       "Logo borttagen (etiketter behålls med tomma logoplatser)",
      logoRenamed:            "Logo omdöpt",
      logoUploaded:           "Logo uppladdad",
      uploadFailed:           "Uppladdning misslyckades",
      deleteFailed:           "Borttagning misslyckades",
      bulkDeleteFailed:       "Massborttagning misslyckades",
      renameFailed:           "Omdöpning misslyckades",
      missing:                "saknades",
      skipped:                "hoppades över",
    },
    freetext: {
      sizes:      "Fritextstorlekar",
      sizesHint:  "Etikettstorlekar som användare kan välja när de skriver ut fritext från surfplattan. Markera en som standard.",
      createSize: "Skapa storlek",
    },
    dialogs: {
      preview: {
        title:             "Förhandsvisning",
        hint:              "Fyll i värden för att se hur etiketten ser ut med riktig data. Använd 'Ladda exempeldata' för att hämta en slumpmässig produkt.",
        loadSample:        "Ladda exempeldata",
        sampleLoaded:      "Exempeldata laddad ({name})",
        sampleFailed:      "Kunde inte ladda exempeldata",
        noSampleAvailable: "Inga aktiva produkter att hämta exempeldata från",
        clearForm:         "Rensa formulär",
        apply:             "Tillämpa",
        noTokens:          "Inga tokens på denna etikett ännu",
        pickDate:          "Välj datum",
        tabDate:           "Datum",
        tabTime:           "Tid",
        now:               "Nu",
        pickProduct:       "Välj produkt",
        searchProduct:     "Sök…",
        noProducts:        "Inga aktiva produkter",
        itemNotFound:      "Produkten finns inte",
        productListFailed: "Kunde inte ladda produktlistan",
      },
      print: {
        title:        "Skriv ut etikett",
        hint:         "Hämta etiketten som PDF eller ZPL.",
        format:       "Format",
        dpi:          "Upplösning",
        noDataHint:   "Ingen exempeldata laddad. Etiketten visas med platshållare. Öppna Förhandsvisning för att ladda riktiga värden.",
        generateZpl:  "Generera ZPL",
        zplOutput:    "ZPL-utdata",
        copyZpl:      "Kopiera ZPL",
        downloadZpl:  "Hämta .zpl",
        copied:       "Kopierat till urklipp",
        copyFailed:   "Kunde inte kopiera",
        pdfSuccess:   "PDF hämtad",
        failed:       "Kunde inte generera",
      },
      logo: {
        title:             "Ladda upp logo",
        selectFile:        "Välj bild",
        fileHint:          "PNG, JPG, GIF eller WebP. Max 5 MB.",
        widthMm:           "Bredd (mm)",
        widthHint:         "Höjden beräknas automatiskt",
        dpi:               "Upplösning",
        threshold:         "Svart/vit-tröskel",
        thresholdHint:     "Lägre = mer vitt. Högre = mer svart.",
        original:          "Original",
        mono:              "Svartvitt",
        apply:             "Tillämpa",
      },
      logoPicker: {
        title:             "Välj logo",
        uploadNew:         "Ladda upp ny",
        search:            "Sök logo...",
        empty:             "Inga logotyper ännu. Ladda upp din första för att komma igång.",
        noMatch:           "Inga logotyper matchar sökningen",
      },
    },
    common: {
      unsaved:  "Osparade ändringar",
      cancel:   "Avbryt",
      close:    "Stäng",
      download: "Hämta",
      clear:    "Rensa",
      refresh:  "Uppdatera",
      delete:   "Ta bort",
      loading:  "Laddar…",
      nutritionProfile:  "Näringsprofil",
      nutritionProfiles: "Näringsprofiler",
    },
  },

  // ─────────────────────────────────────────────────────────
  // Norwegian (no)
  // ─────────────────────────────────────────────────────────
  no: {
    logoLibrary:       "Logobibliotek",
    nutritionProfiles: "Næringsprofiler",

    designer: {
      untitled:        "Uten tittel",
      noLabel:         "Ingen etikett valgt",
      saved:           "Lagret",
      saveError:       "Kunne ikke lagre",
      unsavedTitle:    "Ulagrede endringer",
      unsavedConfirm:  "Du har ulagrede endringer. Vil du forlate siden?",
      leaveAnyway:     "Forlat likevel",
      stay:            "Bli",
      toggleGrid:      "Veksle rutenett",
      preview:         "Forhåndsvisning",
      undo:            "Angre",
      redo:            "Gjør om",
      save:            "Lagre",
      print:           "Skriv ut",
      back:            "Tilbake",
      deleteElement:   "Slett element",
    },
    fields: {
      productFields:  "Produktfelt",
      productField:   "Produktfelt",
      dateLogic:      "Dato & logikk",
      date:           "Dato",
      datePlus:       "Dato + dager",
      valueOnly:      "Kun verdi",
      prompt:         "Inntastings-prompt",
      barcodes:       "Strekkoder",
      barcode:        "Strekkode / QR",
      qrCode:         "QR-kode",
      layout:         "Layout",
      staticText:     "Statisk tekst",
      allergens:      "Allergener",
      nutrition:      "Næringsinnhold",
      imageLogo:      "Logobilde",
      divider:        "Skillelinje",
      productName:    "Produktnavn",
      tokens: {
        product_number: "Produktnummer",
        product_name:   "Produktnavn",
        description:    "Beskrivelse",
        three_hours:    "3-timersregel",
        fridge:         "Oppbevaring",
      },
    },
    label: {
      name:    "Etikettnavn",
      width:   "Bredde (mm)",
      height:  "Høyde (mm)",
      unit:    "Enhet",
      mm:      "mm",
      inch:    "tomme",
      colour:  "Farge",
    },
    canvas: {
      grid:          "Vis rutenett",
      gridSize:      "Rutenettstørrelse (mm)",
      layers:        "Lag",
      offCanvas:     "Utenfor etiketten",
      resetPosition: "Flytt tilbake til 0,0",
      locked:        "Låst",
      unlocked:      "Ulåst",
      layer:         "Lag",
      bringFront:    "Lengst frem",
      sendBack:      "Lengst bak",
      bringForward:  "Ett lag fram",
      sendBackward:  "Ett lag tilbake",
      lockPosition:  "Lås posisjon",
      unlockPosition:"Lås opp",
    },
    element: {
      title:                   "Element",
      position:                "Posisjon",
      size:                    "Størrelse",
      rotation:                "Rotasjon",
      typography:              "Typografi",
      labelText:               "Etiketttekst",
      labelTextHint:           "Vises foran verdien",
      showLabelOn:             "Vis etikett",
      showLabelOff:            "Skjul etikett",
      showLabelToggle:         "Vis etikett",
      font:                    "Skrifttype",
      fontDefault:             "Standard",
      fontSize:                "Skriftstørrelse",
      align:                   "Justering",
      left:                    "Venstre",
      center:                  "Sentrert",
      right:                   "Høyre",
      bold:                    "Fet",
      italic:                  "Kursiv",
      wordWrap:                "Ordbrytning",
      maxLines:                "Maks linjer",
      wrapWidthHint:           "Teksten brytes innenfor elementets bredde (W over)",
      fontBuiltIn:             "standard",
      dateFormat:              "Datoformat",
      dateFormatAuto:          "Følg språk",
      staticSection:           "Statisk tekst",
      staticText:              "Tekst",
      staticTextHint:          "Teksten som skrives ut på etiketten",
      dateSection:             "Dato-innstillinger",
      dateMode:                "Type",
      dateModePrint:           "Utskriftsdato",
      dateModeProduced:        "Produksjonsdato",
      dateModeExpiry:          "Utløpsdato",
      dateModeFixed:           "Spesifikk dato",
      dateBase:                "Kilde",
      producedDate:            "Produksjonsdato",
      openedDate:              "Åpningsdato",
      daysOffsetField:         "Dager fra felt",
      daysOffsetFixed:         "Antall dager",
      daysOffsetFixedOption:   "Fast antall dager",
      fixedDate:               "Velg dato",
      thawingDays:             "Tinedager",
      refrigeratedDays:        "Kjøledager",
      expiryDays:              "Holdbarhetsdager",
      showTime:                "Vis tidspunkt",
      databaseSection:         "Database-felt",
      databaseField:           "Felt",
      dbProductName:           "Produktnavn",
      dbProductNumber:         "Produktnummer",
      dbDescription:           "Beskrivelse",
      dbEanCode:               "EAN / Strekkode",
      dbAllergens:             "Allergener",
      dbNotes:                 "Notater",
      dbFridge:                "Kjøleskapsverdi",
      dbThreeHours:            "Tre timer (Ja/Nei)",
      valueOnlySection:        "Verdikilde",
      valueOnlyType:           "Kildetype",
      valueTypeDate:           "Dato",
      valueTypeDatePlus:       "Dato + offset",
      valueTypeDatabase:       "Database",
      descriptionSection:      "Beskrivelse",
      maxWidth:                "Maks bredde (mm)",
      descriptionMaxWidthHint: "Teksten brytes innenfor denne bredden",
      allergensSection:        "Allergener",
      allergenSeparator:       "Separator",
      allergenSeparatorHint:   'f.eks. ", " eller " · "',
      allergenCase:            "Versaler",
      allergenCaseHint:        "Styrer hvordan allergener vises på etiketten",
      allergenCaseKeep:        "Som angitt",
      allergenCaseLower:       "små bokstaver",
      allergenCaseUpper:       "STORE BOKSTAVER",
      allergenCaseTitle:       "Stor Forbokstav",
      valuePosition:           "Verdiplassering",
      separator:               "Separator",
      separatorHint:           "Tekst mellom etikett og verdi. La stå tom for ingen separator.",
      valueX:                  "Verdi X (mm)",
      valueY:                  "Verdi Y (mm)",
      valueFontSize:           "Verdi-skriftstørrelse",
      barcode:                 "Strekkode",
      barcodeType:             "Strekkode-type",
      dataField:               "Datafelt",
      customData:              "Egendefinert data",
      customDataValue:         "Egendefinert verdi",
      customDataHint:          "Bruk {product_number}, {product_name} som plassholdere",
      showValue:               "Vis verdi",
      qrCode:                  "QR-kode",
      qrSize:                  "Størrelse (mm)",
      qrSquareHint:            "QR-koder er kvadratiske — én størrelse angir begge dimensjoner.",
      customUrl:               "Egendefinert URL / data",
      customUrlHint:           "Bruk {product_number}, {product_name} som plassholdere",
      image:                   "Bilde",
      replaceImage:            "Bytt bilde",
      uploadImage:             "Last opp bilde",
      logoBound:               "Tilknyttet logo",
      chooseImage:             "Velg logo",
      changeImage:             "Bytt logo",
      divider:                 "Skillelinje",
      lineWidth:               "Linjebredde",
      lineThickness:           "Linjetykkelse",
      nutritionSection:        "Næringsinnhold",
      nutritionShowHeader:     "Vis overskrift (Næringsinnhold:)",
      nutritionShowReference:  "Vis referanse (per 100g)",
      nutritionSubIndent:      "Innrykkstegn for underrader",
      nutritionSubIndentHint:  "Vises foran hvorav-rader",
      nutritionRows:           "Synlige rader",
      nutritionDecimals:       "Antall desimaler",
      nutritionDecimalsHint:   "Antall sifre etter kommaet (0-3)",
      nutritionFontWarning:    "Skriftstørrelse under 2,4mm kan være under EU-minimum for x-høyde (1169/2011).",
      promptSection:           "Inntastings-prompt",
      promptToken:             "Inntastningstext",
      promptTokenHint:         "Vises i forhåndsvisning og på etiketten foran et inntastingsfelt",
    },
    nutrition: {
      title:        "Næringsinnhold",
      per_100g:     "per 100g",
      per_100ml:    "per 100ml",
      per_portion:  "per porsjon",
      energy:       "Energi",
      fat:          "Fett",
      saturated:    "hvorav mettede fettsyrer",
      carbs:        "Karbohydrater",
      sugars:       "hvorav sukkerarter",
      protein:      "Protein",
      salt:         "Salt",
      profiles:           "Næringsprofiler",
      profilesHint:       "Opprett gjenbrukbare næringsprofiler som kan knyttes til produkter og vises på etiketter.",
      noProfiles:         "Ingen næringsprofiler opprettet ennå.",
      newProfile:         "Ny profil",
      editProfile:        "Rediger profil",
      profileName:        "Navn",
      reference:          "Referanse",
      portionSize:        "Porsjonsstørrelse (f.eks. 160g)",
      values:             "Næringsverdier",
      required:           "Påkrevd",
      confirmDelete:      "Bekreft sletting",
      confirmDeleteText:  "Slett profil",
      deleteWarning:      "Produkter som bruker denne profilen mister sine næringsdata.",
      errorSaving:        "Kunne ikke lagre profilen",
      errorLoading:       "Kunne ikke hente profiler",
    },
    logos: {
      libraryTitle:           "Logobibliotek",
      libraryHint:            "Logoer som kan legges til på etiketter i designeren. Opplastede logoer kan gjenbrukes på flere etiketter.",
      uploadButton:           "Last opp logo",
      noLogos:                "Ingen logoer ennå. Last opp en for å komme i gang.",
      preview:                "Forhåndsvisning",
      name:                   "Navn",
      dimensions:             "Størrelse",
      usedBy:                 "Brukes av",
      uploaded:               "Lastet opp",
      confirmDeleteLogo:      "Er du sikker på at du vil slette denne logoen?",
      deleteLogoInUseWarning: "Denne logoen er i bruk. Logo-elementene blir igjen på etikettene, men vises tomme inntil en ny logo tilknyttes.",
      confirmBulkDelete:      "Bekreft masseslettning",
      confirmBulkDeleteText:  "Er du sikker på at du vil slette",
      logosWord:              "logo(er)",
      selectedInUseWarning:   "av de valgte logoene er i bruk. Logo-elementene blir igjen på etikettene, men vises tomme inntil en ny logo tilknyttes.",
      andMore:                "og",
      more:                   "flere",
      deleteSelected:         "Slett valgte",
      deleteAll:              "Slett alle",
      logosDeleted:           "Slettet",
      logoDeleted:            "Logo slettet",
      logoDeletedInUse:       "Logo slettet (etiketter beholdt med tomme logoplasser)",
      logoRenamed:            "Logo omdøpt",
      logoUploaded:           "Logo lastet opp",
      uploadFailed:           "Opplasting mislyktes",
      deleteFailed:           "Sletting mislyktes",
      bulkDeleteFailed:       "Masseslettning mislyktes",
      renameFailed:           "Omdøping mislyktes",
      missing:                "manglet",
      skipped:                "hoppet over",
    },
    freetext: {
      sizes:      "Fritekst-størrelser",
      sizesHint:  "Etikettstørrelser brukerne kan velge når de skriver ut fritekst fra nettbrettet. Marker én som standard.",
      createSize: "Opprett størrelse",
    },
    dialogs: {
      preview: {
        title:             "Forhåndsvisning",
        hint:              "Fyll inn verdier for å se hvordan etiketten ser ut med ekte data. Bruk 'Hent prøvedata' for å hente et tilfeldig produkt.",
        loadSample:        "Hent prøvedata",
        sampleLoaded:      "Prøvedata hentet ({name})",
        sampleFailed:      "Kunne ikke hente prøvedata",
        noSampleAvailable: "Ingen aktive produkter å hente prøvedata fra",
        clearForm:         "Tøm skjema",
        apply:             "Bruk",
        noTokens:          "Ingen tokens på denne etiketten ennå",
        pickDate:          "Velg dato",
        tabDate:           "Dato",
        tabTime:           "Tid",
        now:               "Nå",
        pickProduct:       "Velg produkt",
        searchProduct:     "Søk…",
        noProducts:        "Ingen aktive produkter",
        itemNotFound:      "Produktet finnes ikke",
        productListFailed: "Kunne ikke hente produktliste",
      },
      print: {
        title:        "Skriv ut etikett",
        hint:         "Hent etiketten som PDF eller ZPL.",
        format:       "Format",
        dpi:          "Oppløsning",
        noDataHint:   "Ingen prøvedata hentet. Etiketten vises med plassholdere. Åpne Forhåndsvisning for å hente ekte verdier.",
        generateZpl:  "Generer ZPL",
        zplOutput:    "ZPL-utdata",
        copyZpl:      "Kopier ZPL",
        downloadZpl:  "Hent .zpl",
        copied:       "Kopiert til utklippstavle",
        copyFailed:   "Kunne ikke kopiere",
        pdfSuccess:   "PDF hentet",
        failed:       "Kunne ikke generere",
      },
      logo: {
        title:             "Last opp logo",
        selectFile:        "Velg bilde",
        fileHint:          "PNG, JPG, GIF eller WebP. Maks 5 MB.",
        widthMm:           "Bredde (mm)",
        widthHint:         "Høyden beregnes automatisk",
        dpi:               "Oppløsning",
        threshold:         "Svart/hvit-terskel",
        thresholdHint:     "Lavere = mer hvitt. Høyere = mer svart.",
        original:          "Original",
        mono:              "Svart/hvit",
        apply:             "Bruk",
      },
      logoPicker: {
        title:             "Velg logo",
        uploadNew:         "Last opp ny",
        search:            "Søk logo...",
        empty:             "Ingen logoer ennå. Last opp din første for å komme i gang.",
        noMatch:           "Ingen logoer matcher søket",
      },
    },
    common: {
      unsaved:  "Ulagrede endringer",
      cancel:   "Avbryt",
      close:    "Lukk",
      download: "Hent",
      clear:    "Tøm",
      refresh:  "Oppdater",
      delete:   "Slett",
      loading:  "Laster…",
      nutritionProfile:  "Næringsprofil",
      nutritionProfiles: "Næringsprofiler",
    },
  },

  // ─────────────────────────────────────────────────────────
  // Finnish (fi)
  // ─────────────────────────────────────────────────────────
  fi: {
    logoLibrary:       "Logokirjasto",
    nutritionProfiles: "Ravintoprofiilit",

    designer: {
      untitled:        "Nimetön",
      noLabel:         "Etikettiä ei valittu",
      saved:           "Tallennettu",
      saveError:       "Tallennus epäonnistui",
      unsavedTitle:    "Tallentamattomia muutoksia",
      unsavedConfirm:  "Sinulla on tallentamattomia muutoksia. Haluatko poistua sivulta?",
      leaveAnyway:     "Poistu silti",
      stay:            "Pysy",
      toggleGrid:      "Vaihda ruudukko",
      preview:         "Esikatselu",
      undo:            "Kumoa",
      redo:            "Tee uudelleen",
      save:            "Tallenna",
      print:           "Tulosta",
      back:            "Takaisin",
      deleteElement:   "Poista elementti",
    },
    fields: {
      productFields:  "Tuotekentät",
      productField:   "Tuotekenttä",
      dateLogic:      "Päivämäärä & logiikka",
      date:           "Päivämäärä",
      datePlus:       "Päivämäärä + päivät",
      valueOnly:      "Vain arvo",
      prompt:         "Syöttökehote",
      barcodes:       "Viivakoodit",
      barcode:        "Viivakoodi / QR",
      qrCode:         "QR-koodi",
      layout:         "Asettelu",
      staticText:     "Staattinen teksti",
      allergens:      "Allergeenit",
      nutrition:      "Ravintosisältö",
      imageLogo:      "Logo-kuva",
      divider:        "Erotin",
      productName:    "Tuotenimi",
      tokens: {
        product_number: "Tuotenumero",
        product_name:   "Tuotenimi",
        description:    "Kuvaus",
        three_hours:    "3 tunnin sääntö",
        fridge:         "Säilytys",
      },
    },
    label: {
      name:    "Etiketin nimi",
      width:   "Leveys (mm)",
      height:  "Korkeus (mm)",
      unit:    "Yksikkö",
      mm:      "mm",
      inch:    "tuuma",
      colour:  "Väri",
    },
    canvas: {
      grid:          "Näytä ruudukko",
      gridSize:      "Ruudukon koko (mm)",
      layers:        "Tasot",
      offCanvas:     "Etiketin ulkopuolella",
      resetPosition: "Siirrä takaisin kohtaan 0,0",
      locked:        "Lukittu",
      unlocked:      "Avattu",
      layer:         "Taso",
      bringFront:    "Tuo eteen",
      sendBack:      "Vie taakse",
      bringForward:  "Tuo eteenpäin",
      sendBackward:  "Vie taaksepäin",
      lockPosition:  "Lukitse sijainti",
      unlockPosition:"Avaa lukitus",
    },
    element: {
      title:                   "Elementti",
      position:                "Sijainti",
      size:                    "Koko",
      rotation:                "Kierto",
      typography:              "Typografia",
      labelText:               "Etiketin teksti",
      labelTextHint:           "Näytetään arvon edessä",
      showLabelOn:             "Näytä etiketti",
      showLabelOff:            "Piilota etiketti",
      showLabelToggle:         "Näytä etiketti",
      font:                    "Kirjasin",
      fontDefault:             "Oletus",
      fontSize:                "Kirjasinkoko",
      align:                   "Tasaus",
      left:                    "Vasen",
      center:                  "Keskellä",
      right:                   "Oikea",
      bold:                    "Lihavoitu",
      italic:                  "Kursiivi",
      wordWrap:                "Tekstin rivitys",
      maxLines:                "Maks. rivit",
      wrapWidthHint:           "Teksti rivittyy elementin leveyden sisällä (W yllä)",
      fontBuiltIn:             "oletus",
      dateFormat:              "Päivämäärämuoto",
      dateFormatAuto:          "Seuraa kieltä",
      staticSection:           "Staattinen teksti",
      staticText:              "Teksti",
      staticTextHint:          "Etikettiin tulostettava teksti",
      dateSection:             "Päivämääräasetukset",
      dateMode:                "Tyyppi",
      dateModePrint:           "Tulostuspäivä",
      dateModeProduced:        "Valmistuspäivä",
      dateModeExpiry:          "Vanhentumispäivä",
      dateModeFixed:           "Tietty päivämäärä",
      dateBase:                "Lähde",
      producedDate:            "Valmistuspäivä",
      openedDate:              "Avauspäivä",
      daysOffsetField:         "Päivät kentästä",
      daysOffsetFixed:         "Päivien määrä",
      daysOffsetFixedOption:   "Kiinteä määrä päiviä",
      fixedDate:               "Valitse päivämäärä",
      thawingDays:             "Sulatuspäivät",
      refrigeratedDays:        "Jääkaappipäivät",
      expiryDays:              "Säilyvyyspäivät",
      showTime:                "Näytä kellonaika",
      databaseSection:         "Tietokantakenttä",
      databaseField:           "Kenttä",
      dbProductName:           "Tuotenimi",
      dbProductNumber:         "Tuotenumero",
      dbDescription:           "Kuvaus",
      dbEanCode:               "EAN / Viivakoodi",
      dbAllergens:             "Allergeenit",
      dbNotes:                 "Muistiinpanot",
      dbFridge:                "Jääkaappiarvo",
      dbThreeHours:            "Kolme tuntia (Kyllä/Ei)",
      valueOnlySection:        "Arvon lähde",
      valueOnlyType:           "Lähdetyyppi",
      valueTypeDate:           "Päivämäärä",
      valueTypeDatePlus:       "Päivämäärä + offset",
      valueTypeDatabase:       "Tietokanta",
      descriptionSection:      "Kuvaus",
      maxWidth:                "Max leveys (mm)",
      descriptionMaxWidthHint: "Teksti rivittyy tämän leveyden sisällä",
      allergensSection:        "Allergeenit",
      allergenSeparator:       "Erotin",
      allergenSeparatorHint:   'esim. ", " tai " · "',
      allergenCase:            "Kirjainkoko",
      allergenCaseHint:        "Hallitsee allergeenien näkymistä etiketissä",
      allergenCaseKeep:        "Kuten syötetty",
      allergenCaseLower:       "pienet kirjaimet",
      allergenCaseUpper:       "ISOT KIRJAIMET",
      allergenCaseTitle:       "Alkukirjain Isolla",
      valuePosition:           "Arvon sijainti",
      separator:               "Erotin",
      separatorHint:           "Teksti etiketin ja arvon välissä. Jätä tyhjäksi jos ei erotinta.",
      valueX:                  "Arvo X (mm)",
      valueY:                  "Arvo Y (mm)",
      valueFontSize:           "Arvon kirjasinkoko",
      barcode:                 "Viivakoodi",
      barcodeType:             "Viivakoodityyppi",
      dataField:               "Datakenttä",
      customData:              "Mukautettu data",
      customDataValue:         "Mukautettu arvo",
      customDataHint:          "Käytä {product_number}, {product_name} paikkamerkkeinä",
      showValue:               "Näytä arvo",
      qrCode:                  "QR-koodi",
      qrSize:                  "Koko (mm)",
      qrSquareHint:            "QR-koodit ovat neliön muotoisia — yksi koko asettaa molemmat mitat.",
      customUrl:               "Mukautettu URL / data",
      customUrlHint:           "Käytä {product_number}, {product_name} paikkamerkkeinä",
      image:                   "Kuva",
      replaceImage:            "Vaihda kuva",
      uploadImage:             "Lataa kuva",
      logoBound:               "Sidottu logo",
      chooseImage:             "Valitse logo",
      changeImage:             "Vaihda logo",
      divider:                 "Erotin",
      lineWidth:               "Viivan leveys",
      lineThickness:           "Viivan paksuus",
      nutritionSection:        "Ravintosisältö",
      nutritionShowHeader:     "Näytä otsikko (Ravintosisältö:)",
      nutritionShowReference:  "Näytä viite (per 100g)",
      nutritionSubIndent:      "Alirivien sisennysmerkki",
      nutritionSubIndentHint:  "Näytetään josta-rivien edessä",
      nutritionRows:           "Näkyvät rivit",
      nutritionDecimals:       "Desimaalien määrä",
      nutritionDecimalsHint:   "Desimaalierottimen jälkeisten numeroiden määrä (0-3)",
      nutritionFontWarning:    "Kirjasinkoko alle 2,4mm voi olla alle EU:n minimi x-korkeudelle (1169/2011).",
      promptSection:           "Syöttökehote",
      promptToken:             "Syöttöteksti",
      promptTokenHint:         "Näytetään esikatselussa ja etiketissä syöttökentän edessä",
    },
    nutrition: {
      title:        "Ravintosisältö",
      per_100g:     "per 100g",
      per_100ml:    "per 100ml",
      per_portion:  "per annos",
      energy:       "Energia",
      fat:          "Rasva",
      saturated:    "josta tyydyttynyttä",
      carbs:        "Hiilihydraatit",
      sugars:       "josta sokereita",
      protein:      "Proteiini",
      salt:         "Suola",
      profiles:           "Ravintoprofiilit",
      profilesHint:       "Luo uudelleenkäytettäviä ravintoprofiileja, jotka voidaan liittää tuotteisiin ja näyttää etiketeissä.",
      noProfiles:         "Ravintoprofiileja ei vielä luotu.",
      newProfile:         "Uusi profiili",
      editProfile:        "Muokkaa profiilia",
      profileName:        "Nimi",
      reference:          "Viite",
      portionSize:        "Annoskoko (esim. 160g)",
      values:             "Ravintoarvot",
      required:           "Pakollinen",
      confirmDelete:      "Vahvista poisto",
      confirmDeleteText:  "Poista profiili",
      deleteWarning:      "Tuotteet, jotka käyttävät tätä profiilia, menettävät ravintotietonsa.",
      errorSaving:        "Profiilin tallennus epäonnistui",
      errorLoading:       "Profiilien lataus epäonnistui",
    },
    logos: {
      libraryTitle:           "Logokirjasto",
      libraryHint:            "Logot, jotka voidaan lisätä etiketteihin suunnittelijassa. Ladattuja logoja voidaan käyttää useissa etiketeissä.",
      uploadButton:           "Lataa logo",
      noLogos:                "Ei vielä logoja. Lataa yksi aloittaaksesi.",
      preview:                "Esikatselu",
      name:                   "Nimi",
      dimensions:             "Koko",
      usedBy:                 "Käyttää",
      uploaded:               "Ladattu",
      confirmDeleteLogo:      "Haluatko varmasti poistaa tämän logon?",
      deleteLogoInUseWarning: "Tämä logo on käytössä. Logo-elementit pysyvät etiketeissä mutta näkyvät tyhjinä, kunnes uusi logo sidotaan.",
      confirmBulkDelete:      "Vahvista joukkopoisto",
      confirmBulkDeleteText:  "Haluatko varmasti poistaa",
      logosWord:              "logoa",
      selectedInUseWarning:   "valituista logoista on käytössä. Logo-elementit pysyvät etiketeissä mutta näkyvät tyhjinä, kunnes uusi logo sidotaan.",
      andMore:                "ja",
      more:                   "lisää",
      deleteSelected:         "Poista valitut",
      deleteAll:              "Poista kaikki",
      logosDeleted:           "Poistettu",
      logoDeleted:            "Logo poistettu",
      logoDeletedInUse:       "Logo poistettu (etiketit säilytetty tyhjillä logopaikoilla)",
      logoRenamed:            "Logo nimetty uudelleen",
      logoUploaded:           "Logo ladattu",
      uploadFailed:           "Lataus epäonnistui",
      deleteFailed:           "Poisto epäonnistui",
      bulkDeleteFailed:       "Joukkopoisto epäonnistui",
      renameFailed:           "Uudelleennimeäminen epäonnistui",
      missing:                "puuttui",
      skipped:                "ohitettu",
    },
    freetext: {
      sizes:      "Vapaatekstikoot",
      sizesHint:  "Etikettikoot, joista käyttäjät voivat valita tulostaessaan vapaata tekstiä tabletilta. Merkitse yksi oletukseksi.",
      createSize: "Luo koko",
    },
    dialogs: {
      preview: {
        title:             "Esikatselu",
        hint:              "Syötä arvot nähdäksesi, miltä etiketti näyttää oikeilla tiedoilla. Käytä 'Lataa näytetiedot' satunnaisen tuotteen hakemiseen.",
        loadSample:        "Lataa näytetiedot",
        sampleLoaded:      "Näytetiedot ladattu ({name})",
        sampleFailed:      "Näytetietojen lataus epäonnistui",
        noSampleAvailable: "Ei aktiivisia tuotteita näytetiedoiksi",
        clearForm:         "Tyhjennä lomake",
        apply:             "Käytä",
        noTokens:          "Tässä etiketissä ei ole vielä tokeneita",
        pickDate:          "Valitse päivämäärä",
        tabDate:           "Päivämäärä",
        tabTime:           "Aika",
        now:               "Nyt",
        pickProduct:       "Valitse tuote",
        searchProduct:     "Hae…",
        noProducts:        "Ei aktiivisia tuotteita",
        itemNotFound:      "Tuotetta ei löydy",
        productListFailed: "Tuotelistan lataus epäonnistui",
      },
      print: {
        title:        "Tulosta etiketti",
        hint:         "Lataa etiketti PDF- tai ZPL-muodossa.",
        format:       "Muoto",
        dpi:          "Tarkkuus",
        noDataHint:   "Näytetietoja ei ole ladattu. Etiketissä näytetään paikkamerkit. Avaa Esikatselu ladataksesi todelliset arvot.",
        generateZpl:  "Luo ZPL",
        zplOutput:    "ZPL-tulos",
        copyZpl:      "Kopioi ZPL",
        downloadZpl:  "Lataa .zpl",
        copied:       "Kopioitu leikepöydälle",
        copyFailed:   "Kopiointi epäonnistui",
        pdfSuccess:   "PDF ladattu",
        failed:       "Luonti epäonnistui",
      },
      logo: {
        title:             "Lataa logo",
        selectFile:        "Valitse kuva",
        fileHint:          "PNG, JPG, GIF tai WebP. Maks 5 MB.",
        widthMm:           "Leveys (mm)",
        widthHint:         "Korkeus lasketaan automaattisesti",
        dpi:               "Tarkkuus",
        threshold:         "Mustavalkoinen-kynnys",
        thresholdHint:     "Pienempi = enemmän valkoista. Suurempi = enemmän mustaa.",
        original:          "Alkuperäinen",
        mono:              "Mustavalko",
        apply:             "Käytä",
      },
      logoPicker: {
        title:             "Valitse logo",
        uploadNew:         "Lataa uusi",
        search:            "Etsi logoa...",
        empty:             "Ei vielä logoja. Lataa ensimmäinen aloittaaksesi.",
        noMatch:           "Mikään logo ei vastaa hakua",
      },
    },
    common: {
      unsaved:  "Tallentamattomia muutoksia",
      cancel:   "Peruuta",
      close:    "Sulje",
      download: "Lataa",
      clear:    "Tyhjennä",
      refresh:  "Päivitä",
      delete:   "Poista",
      loading:  "Ladataan…",
      nutritionProfile:  "Ravintoprofiili",
      nutritionProfiles: "Ravintoprofiilit",
    },
  },

  // ─────────────────────────────────────────────────────────
  // German (de)
  // ─────────────────────────────────────────────────────────
  de: {
    logoLibrary:       "Logo-Bibliothek",
    nutritionProfiles: "Nährwertprofile",

    designer: {
      untitled:        "Unbenannt",
      noLabel:         "Kein Etikett ausgewählt",
      saved:           "Gespeichert",
      saveError:       "Speichern fehlgeschlagen",
      unsavedTitle:    "Ungespeicherte Änderungen",
      unsavedConfirm:  "Sie haben ungespeicherte Änderungen. Möchten Sie die Seite verlassen?",
      leaveAnyway:     "Trotzdem verlassen",
      stay:            "Bleiben",
      toggleGrid:      "Raster umschalten",
      preview:         "Vorschau",
      undo:            "Rückgängig",
      redo:            "Wiederholen",
      save:            "Speichern",
      print:           "Drucken",
      back:            "Zurück",
      deleteElement:   "Element löschen",
    },
    fields: {
      productFields:  "Produktfelder",
      productField:   "Produktfeld",
      dateLogic:      "Datum & Logik",
      date:           "Datum",
      datePlus:       "Datum + Tage",
      valueOnly:      "Nur Wert",
      prompt:         "Eingabeaufforderung",
      barcodes:       "Barcodes",
      barcode:        "Barcode / QR",
      qrCode:         "QR-Code",
      layout:         "Layout",
      staticText:     "Statischer Text",
      allergens:      "Allergene",
      nutrition:      "Nährwerte",
      imageLogo:      "Logo-Bild",
      divider:        "Trennlinie",
      productName:    "Produktname",
      tokens: {
        product_number: "Produktnummer",
        product_name:   "Produktname",
        description:    "Beschreibung",
        three_hours:    "3-Stunden-Regel",
        fridge:         "Kühlschrank",
      },
    },
    label: {
      name:    "Etikettname",
      width:   "Breite (mm)",
      height:  "Höhe (mm)",
      unit:    "Einheit",
      mm:      "mm",
      inch:    "Zoll",
      colour:  "Farbe",
    },
    canvas: {
      grid:          "Raster anzeigen",
      gridSize:      "Rastergröße (mm)",
      layers:        "Ebenen",
      offCanvas:     "Außerhalb des Etiketts",
      resetPosition: "Zurück zu 0,0",
      locked:        "Gesperrt",
      unlocked:      "Entsperrt",
      layer:         "Ebene",
      bringFront:    "In den Vordergrund",
      sendBack:      "In den Hintergrund",
      bringForward:  "Eine Ebene nach vorn",
      sendBackward:  "Eine Ebene nach hinten",
      lockPosition:  "Position sperren",
      unlockPosition:"Entsperren",
    },
    element: {
      title:                   "Element",
      position:                "Position",
      size:                    "Größe",
      rotation:                "Drehung",
      typography:              "Typografie",
      labelText:               "Etikettentext",
      labelTextHint:           "Wird vor dem Wert angezeigt",
      showLabelOn:             "Etikett anzeigen",
      showLabelOff:            "Etikett ausblenden",
      showLabelToggle:         "Etikett anzeigen",
      font:                    "Schriftart",
      fontDefault:             "Standard",
      fontSize:                "Schriftgröße",
      align:                   "Ausrichtung",
      left:                    "Links",
      center:                  "Mittig",
      right:                   "Rechts",
      bold:                    "Fett",
      italic:                  "Kursiv",
      wordWrap:                "Zeilenumbruch",
      maxLines:                "Max. Zeilen",
      wrapWidthHint:           "Text wird innerhalb der Elementbreite umbrochen (W oben)",
      fontBuiltIn:             "Standard",
      dateFormat:              "Datumsformat",
      dateFormatAuto:          "Sprache folgen",
      staticSection:           "Statischer Text",
      staticText:              "Text",
      staticTextHint:          "Der auf dem Etikett gedruckte Text",
      dateSection:             "Datum-Einstellungen",
      dateMode:                "Typ",
      dateModePrint:           "Druckdatum",
      dateModeProduced:        "Produktionsdatum",
      dateModeExpiry:          "Verfallsdatum",
      dateModeFixed:           "Bestimmtes Datum",
      dateBase:                "Quelle",
      producedDate:            "Produktionsdatum",
      openedDate:              "Öffnungsdatum",
      daysOffsetField:         "Tage aus Feld",
      daysOffsetFixed:         "Anzahl Tage",
      daysOffsetFixedOption:   "Feste Anzahl Tage",
      fixedDate:               "Datum wählen",
      thawingDays:             "Auftautage",
      refrigeratedDays:        "Kühltage",
      expiryDays:              "Haltbarkeitstage",
      showTime:                "Uhrzeit anzeigen",
      databaseSection:         "Datenbank-Feld",
      databaseField:           "Feld",
      dbProductName:           "Produktname",
      dbProductNumber:         "Produktnummer",
      dbDescription:           "Beschreibung",
      dbEanCode:               "EAN / Barcode",
      dbAllergens:             "Allergene",
      dbNotes:                 "Notizen",
      dbFridge:                "Kühlschrank-Wert",
      dbThreeHours:            "Drei Stunden (Ja/Nein)",
      valueOnlySection:        "Wertquelle",
      valueOnlyType:           "Quellentyp",
      valueTypeDate:           "Datum",
      valueTypeDatePlus:       "Datum + Offset",
      valueTypeDatabase:       "Datenbank",
      descriptionSection:      "Beschreibung",
      maxWidth:                "Max. Breite (mm)",
      descriptionMaxWidthHint: "Text wird innerhalb dieser Breite umgebrochen",
      allergensSection:        "Allergene",
      allergenSeparator:       "Trenner",
      allergenSeparatorHint:   'z.B. ", " oder " · "',
      allergenCase:            "Groß-/Kleinschreibung",
      allergenCaseHint:        "Steuert, wie Allergene auf dem Etikett angezeigt werden",
      allergenCaseKeep:        "Wie eingegeben",
      allergenCaseLower:       "kleinbuchstaben",
      allergenCaseUpper:       "GROSSBUCHSTABEN",
      allergenCaseTitle:       "Erster Buchstabe Groß",
      valuePosition:           "Wertposition",
      separator:               "Trenner",
      separatorHint:           "Text zwischen Etikett und Wert. Leer lassen für keinen Trenner.",
      valueX:                  "Wert X (mm)",
      valueY:                  "Wert Y (mm)",
      valueFontSize:           "Wert-Schriftgröße",
      barcode:                 "Barcode",
      barcodeType:             "Barcode-Typ",
      dataField:               "Datenfeld",
      customData:              "Eigene Daten",
      customDataValue:         "Eigener Wert",
      customDataHint:          "Verwenden Sie {product_number}, {product_name} als Platzhalter",
      showValue:               "Wert anzeigen",
      qrCode:                  "QR-Code",
      qrSize:                  "Größe (mm)",
      qrSquareHint:            "QR-Codes sind quadratisch — eine Größe legt beide Maße fest.",
      customUrl:               "Eigene URL / Daten",
      customUrlHint:           "Verwenden Sie {product_number}, {product_name} als Platzhalter",
      image:                   "Bild",
      replaceImage:            "Bild ersetzen",
      uploadImage:             "Bild hochladen",
      logoBound:               "Verknüpftes Logo",
      chooseImage:             "Logo auswählen",
      changeImage:             "Logo ändern",
      divider:                 "Trennlinie",
      lineWidth:               "Linienbreite",
      lineThickness:           "Liniendicke",
      nutritionSection:        "Nährwerte",
      nutritionShowHeader:     "Überschrift anzeigen (Nährwerte:)",
      nutritionShowReference:  "Referenz anzeigen (pro 100g)",
      nutritionSubIndent:      "Einrückzeichen für Unterzeilen",
      nutritionSubIndentHint:  "Erscheint vor davon-Zeilen",
      nutritionRows:           "Sichtbare Zeilen",
      nutritionDecimals:       "Dezimalstellen",
      nutritionDecimalsHint:   "Anzahl der Stellen nach dem Trennzeichen (0-3)",
      nutritionFontWarning:    "Schriftgröße unter 2,4mm kann unter dem EU-Minimum für x-Höhe liegen (1169/2011).",
      promptSection:           "Eingabeaufforderung",
      promptToken:             "Eingabetext",
      promptTokenHint:         "Wird in Vorschau und auf Etikett vor dem Eingabefeld angezeigt",
    },
    nutrition: {
      title:        "Nährwerte",
      per_100g:     "pro 100g",
      per_100ml:    "pro 100ml",
      per_portion:  "pro Portion",
      energy:       "Brennwert",
      fat:          "Fett",
      saturated:    "davon gesättigte Fettsäuren",
      carbs:        "Kohlenhydrate",
      sugars:       "davon Zucker",
      protein:      "Eiweiß",
      salt:         "Salz",
      profiles:           "Nährwertprofile",
      profilesHint:       "Erstellen Sie wiederverwendbare Nährwertprofile, die mit Produkten verknüpft und auf Etiketten angezeigt werden können.",
      noProfiles:         "Noch keine Nährwertprofile erstellt.",
      newProfile:         "Neues Profil",
      editProfile:        "Profil bearbeiten",
      profileName:        "Name",
      reference:          "Referenz",
      portionSize:        "Portionsgröße (z.B. 160g)",
      values:             "Nährwerte",
      required:           "Erforderlich",
      confirmDelete:      "Löschen bestätigen",
      confirmDeleteText:  "Profil löschen",
      deleteWarning:      "Produkte, die dieses Profil verwenden, verlieren ihre Nährwertdaten.",
      errorSaving:        "Profil konnte nicht gespeichert werden",
      errorLoading:       "Profile konnten nicht geladen werden",
    },
    logos: {
      libraryTitle:           "Logo-Bibliothek",
      libraryHint:            "Logos, die im Designer zu Etiketten hinzugefügt werden können. Hochgeladene Logos können auf mehreren Etiketten wiederverwendet werden.",
      uploadButton:           "Logo hochladen",
      noLogos:                "Noch keine Logos. Laden Sie eines hoch, um zu beginnen.",
      preview:                "Vorschau",
      name:                   "Name",
      dimensions:             "Größe",
      usedBy:                 "Verwendet von",
      uploaded:               "Hochgeladen",
      confirmDeleteLogo:      "Möchten Sie dieses Logo wirklich löschen?",
      deleteLogoInUseWarning: "Dieses Logo wird verwendet. Logo-Elemente bleiben auf den Etiketten, werden aber leer angezeigt, bis ein neues Logo verknüpft wird.",
      confirmBulkDelete:      "Massenlöschung bestätigen",
      confirmBulkDeleteText:  "Möchten Sie wirklich löschen",
      logosWord:              "Logo(s)",
      selectedInUseWarning:   "der ausgewählten Logos werden verwendet. Logo-Elemente bleiben auf den Etiketten, werden aber leer angezeigt, bis ein neues Logo verknüpft wird.",
      andMore:                "und",
      more:                   "weitere",
      deleteSelected:         "Ausgewählte löschen",
      deleteAll:              "Alle löschen",
      logosDeleted:           "Gelöscht",
      logoDeleted:            "Logo gelöscht",
      logoDeletedInUse:       "Logo gelöscht (Etiketten mit leeren Logo-Plätzen behalten)",
      logoRenamed:            "Logo umbenannt",
      logoUploaded:           "Logo hochgeladen",
      uploadFailed:           "Hochladen fehlgeschlagen",
      deleteFailed:           "Löschen fehlgeschlagen",
      bulkDeleteFailed:       "Massenlöschung fehlgeschlagen",
      renameFailed:           "Umbenennen fehlgeschlagen",
      missing:                "fehlten",
      skipped:                "übersprungen",
    },
    freetext: {
      sizes:      "Freitext-Größen",
      sizesHint:  "Etikettengrößen, die Benutzer beim Drucken von Freitext vom Tablet auswählen können. Markieren Sie eine als Standard.",
      createSize: "Größe erstellen",
    },
    dialogs: {
      preview: {
        title:             "Vorschau",
        hint:              "Geben Sie Werte ein, um zu sehen, wie das Etikett mit echten Daten aussieht. Verwenden Sie 'Beispieldaten laden', um ein zufälliges Produkt zu laden.",
        loadSample:        "Beispieldaten laden",
        sampleLoaded:      "Beispieldaten geladen ({name})",
        sampleFailed:      "Beispieldaten konnten nicht geladen werden",
        noSampleAvailable: "Keine aktiven Produkte als Beispieldaten verfügbar",
        clearForm:         "Formular leeren",
        apply:             "Anwenden",
        noTokens:          "Noch keine Tokens auf diesem Etikett",
        pickDate:          "Datum wählen",
        tabDate:           "Datum",
        tabTime:           "Zeit",
        now:               "Jetzt",
        pickProduct:       "Produkt wählen",
        searchProduct:     "Suchen…",
        noProducts:        "Keine aktiven Produkte",
        itemNotFound:      "Produkt nicht gefunden",
        productListFailed: "Produktliste konnte nicht geladen werden",
      },
      print: {
        title:        "Etikett drucken",
        hint:         "Etikett als PDF oder ZPL herunterladen.",
        format:       "Format",
        dpi:          "Auflösung",
        noDataHint:   "Keine Beispieldaten geladen. Das Etikett wird mit Platzhaltern dargestellt. Öffnen Sie die Vorschau, um echte Werte zu laden.",
        generateZpl:  "ZPL generieren",
        zplOutput:    "ZPL-Ausgabe",
        copyZpl:      "ZPL kopieren",
        downloadZpl:  "Als .zpl laden",
        copied:       "In Zwischenablage kopiert",
        copyFailed:   "Kopieren fehlgeschlagen",
        pdfSuccess:   "PDF heruntergeladen",
        failed:       "Erzeugen fehlgeschlagen",
      },
      logo: {
        title:             "Logo hochladen",
        selectFile:        "Bild auswählen",
        fileHint:          "PNG, JPG, GIF oder WebP. Max. 5 MB.",
        widthMm:           "Breite (mm)",
        widthHint:         "Höhe wird automatisch berechnet",
        dpi:               "Auflösung",
        threshold:         "Schwarz/Weiß-Schwelle",
        thresholdHint:     "Niedriger = mehr Weiß. Höher = mehr Schwarz.",
        original:          "Original",
        mono:              "Schwarz/Weiß",
        apply:             "Anwenden",
      },
      logoPicker: {
        title:             "Logo auswählen",
        uploadNew:         "Neues hochladen",
        search:            "Logo suchen...",
        empty:             "Noch keine Logos. Laden Sie Ihr erstes hoch, um zu beginnen.",
        noMatch:           "Keine Logos passen zur Suche",
      },
    },
    common: {
      unsaved:  "Ungespeicherte Änderungen",
      cancel:   "Abbrechen",
      close:    "Schließen",
      download: "Laden",
      clear:    "Leeren",
      refresh:  "Aktualisieren",
      delete:   "Löschen",
      loading:  "Lädt…",
      nutritionProfile:  "Nährwertprofil",
      nutritionProfiles: "Nährwertprofile",
    },
  },

  // ─────────────────────────────────────────────────────────
  // Japanese (ja)
  // ─────────────────────────────────────────────────────────
  ja: {
    logoLibrary:       "ロゴライブラリ",
    nutritionProfiles: "栄養プロファイル",

    designer: {
      untitled:        "無題",
      noLabel:         "ラベルが選択されていません",
      saved:           "保存しました",
      saveError:       "保存できませんでした",
      unsavedTitle:    "未保存の変更",
      unsavedConfirm:  "未保存の変更があります。ページを離れますか?",
      leaveAnyway:     "それでも離れる",
      stay:            "とどまる",
      toggleGrid:      "グリッドの切り替え",
      preview:         "プレビュー",
      undo:            "元に戻す",
      redo:            "やり直し",
      save:            "保存",
      print:           "印刷",
      back:            "戻る",
      deleteElement:   "要素を削除",
    },
    fields: {
      productFields:  "商品フィールド",
      productField:   "商品フィールド",
      dateLogic:      "日付とロジック",
      date:           "日付",
      datePlus:       "日付 + 日数",
      valueOnly:      "値のみ",
      prompt:         "入力プロンプト",
      barcodes:       "バーコード",
      barcode:        "バーコード / QR",
      qrCode:         "QRコード",
      layout:         "レイアウト",
      staticText:     "固定テキスト",
      allergens:      "アレルゲン",
      nutrition:      "栄養成分",
      imageLogo:      "ロゴ画像",
      divider:        "区切り線",
      productName:    "商品名",
      tokens: {
        product_number: "商品番号",
        product_name:   "商品名",
        description:    "説明",
        three_hours:    "3時間ルール",
        fridge:         "冷蔵庫",
      },  
    },
    label: {
      name:    "ラベル名",
      width:   "幅 (mm)",
      height:  "高さ (mm)",
      unit:    "単位",
      mm:      "mm",
      inch:    "インチ",
      colour:  "色",
    },
    canvas: {
      grid:          "グリッドを表示",
      gridSize:      "グリッドサイズ (mm)",
      layers:        "レイヤー",
      offCanvas:     "ラベル範囲外",
      resetPosition: "0,0 に戻す",
      locked:        "ロック",
      unlocked:      "ロック解除",
      layer:         "レイヤー",
      bringFront:    "最前面へ",
      sendBack:      "最背面へ",
      bringForward:  "前面へ",
      sendBackward:  "背面へ",
      lockPosition:  "位置をロック",
      unlockPosition:"ロック解除",
    },
    element: {
      title:                   "要素",
      position:                "位置",
      size:                    "サイズ",
      rotation:                "回転",
      typography:              "タイポグラフィ",
      labelText:               "ラベルテキスト",
      labelTextHint:           "値の前に表示されます",
      showLabelOn:             "ラベルを表示",
      showLabelOff:            "ラベルを非表示",
      showLabelToggle:         "ラベルを表示",
      font:                    "フォント",
      fontDefault:             "デフォルト",
      fontSize:                "フォントサイズ",
      align:                   "整列",
      left:                    "左",
      center:                  "中央",
      right:                   "右",
      bold:                    "太字",
      italic:                  "斜体",
      wordWrap:                "テキストの折り返し",
      maxLines:                "最大行数",
      wrapWidthHint:           "テキストは要素の幅（上のW）内で折り返されます",
      fontBuiltIn:             "標準",
      dateFormat:              "日付形式",
      dateFormatAuto:          "言語に従う",
      staticSection:           "固定テキスト",
      staticText:              "テキスト",
      staticTextHint:          "ラベルに印刷されるテキスト",
      dateSection:             "日付設定",
      dateMode:                "タイプ",
      dateModePrint:           "印刷日",
      dateModeProduced:        "製造日",
      dateModeExpiry:          "賞味期限",
      dateModeFixed:           "特定の日付",
      dateBase:                "ソース",
      producedDate:            "製造日",
      openedDate:              "開封日",
      daysOffsetField:         "フィールドから日数",
      daysOffsetFixed:         "日数",
      daysOffsetFixedOption:   "固定日数",
      fixedDate:               "日付を選択",
      thawingDays:             "解凍日数",
      refrigeratedDays:        "冷蔵日数",
      expiryDays:              "賞味期限日数",
      showTime:                "時刻を表示",
      databaseSection:         "データベースフィールド",
      databaseField:           "フィールド",
      dbProductName:           "商品名",
      dbProductNumber:         "商品番号",
      dbDescription:           "説明",
      dbEanCode:               "EAN / バーコード",
      dbAllergens:             "アレルゲン",
      dbNotes:                 "メモ",
      dbFridge:                "冷蔵庫の値",
      dbThreeHours:            "3時間 (はい/いいえ)",
      valueOnlySection:        "値のソース",
      valueOnlyType:           "ソースタイプ",
      valueTypeDate:           "日付",
      valueTypeDatePlus:       "日付 + オフセット",
      valueTypeDatabase:       "データベース",
      descriptionSection:      "説明",
      maxWidth:                "最大幅 (mm)",
      descriptionMaxWidthHint: "テキストはこの幅内で折り返されます",
      allergensSection:        "アレルゲン",
      allergenSeparator:       "区切り文字",
      allergenSeparatorHint:   '例: ", " または " · "',
      allergenCase:            "大文字小文字",
      allergenCaseHint:        "ラベル上でアレルゲンの表示方法を制御します",
      allergenCaseKeep:        "入力どおり",
      allergenCaseLower:       "すべて小文字",
      allergenCaseUpper:       "すべて大文字",
      allergenCaseTitle:       "先頭大文字",
      valuePosition:           "値の位置",
      separator:               "区切り文字",
      separatorHint:           "ラベルと値の間のテキスト。区切り文字がない場合は空にしてください。",
      valueX:                  "値 X (mm)",
      valueY:                  "値 Y (mm)",
      valueFontSize:           "値のフォントサイズ",
      barcode:                 "バーコード",
      barcodeType:             "バーコードタイプ",
      dataField:               "データフィールド",
      customData:              "カスタムデータ",
      customDataValue:         "カスタム値",
      customDataHint:          "プレースホルダーとして {product_number}, {product_name} を使用",
      showValue:               "値を表示",
      qrCode:                  "QRコード",
      qrSize:                  "サイズ (mm)",
      qrSquareHint:            "QRコードは正方形です — 1つのサイズで両方の寸法が設定されます。",
      customUrl:               "カスタム URL / データ",
      customUrlHint:           "プレースホルダーとして {product_number}, {product_name} を使用",
      image:                   "画像",
      replaceImage:            "画像を置き換える",
      uploadImage:             "画像をアップロード",
      logoBound:               "リンクされたロゴ",
      chooseImage:             "ロゴを選択",
      changeImage:             "ロゴを変更",
      divider:                 "区切り線",
      lineWidth:               "線の幅",
      lineThickness:           "線の太さ",
      nutritionSection:        "栄養成分",
      nutritionShowHeader:     "見出しを表示 (栄養成分:)",
      nutritionShowReference:  "基準量を表示 (100gあたり)",
      nutritionSubIndent:      "サブ行のインデント記号",
      nutritionSubIndentHint:  "うち-行の前に表示されます",
      nutritionRows:           "表示する行",
      nutritionDecimals:       "小数桁数",
      nutritionDecimalsHint:   "小数点以下の桁数 (0-3)",
      nutritionFontWarning:    "フォントサイズ 2.4mm 未満は EU x-高さ最小値を下回る可能性があります (1169/2011)。", 
      promptSection:           "入力プロンプト",
      promptToken:             "入力テキスト",
      promptTokenHint:         "プレビューとラベルで入力フィールドの前に表示されます",
    },
    nutrition: {
      title:        "栄養成分",
      per_100g:     "100gあたり",
      per_100ml:    "100mlあたり",
      per_portion:  "1食あたり",
      energy:       "エネルギー",
      fat:          "脂質",
      saturated:    "うち飽和脂肪酸",
      carbs:        "炭水化物",
      sugars:       "うち糖類",
      protein:      "たんぱく質",
      salt:         "食塩相当量",
      profiles:           "栄養プロファイル",
      profilesHint:       "製品にリンクし、ラベルに表示できる再利用可能な栄養プロファイルを作成します。",
      noProfiles:         "栄養プロファイルがまだ作成されていません。",
      newProfile:         "新規プロファイル",
      editProfile:        "プロファイルを編集",
      profileName:        "名前",
      reference:          "基準量",
      portionSize:        "1食分のサイズ (例: 160g)",
      values:             "栄養価",
      required:           "必須",
      confirmDelete:      "削除を確認",
      confirmDeleteText:  "プロファイルを削除",
      deleteWarning:      "このプロファイルを使用している製品は栄養データを失います。",
      errorSaving:        "プロファイルを保存できませんでした",
      errorLoading:       "プロファイルを読み込めませんでした",
    },
    logos: {
      libraryTitle:           "ロゴライブラリ",
      libraryHint:            "デザイナーでラベルに追加できるロゴ。アップロードしたロゴは複数のラベルで再利用できます。",
      uploadButton:           "ロゴをアップロード",
      noLogos:                "ロゴはまだありません。1つアップロードして始めてください。",
      preview:                "プレビュー",
      name:                   "名前",
      dimensions:             "サイズ",
      usedBy:                 "使用中",
      uploaded:               "アップロード日",
      confirmDeleteLogo:      "このロゴを削除してもよろしいですか?",
      deleteLogoInUseWarning: "このロゴは使用中です。ロゴ要素はラベルに残りますが、新しいロゴが割り当てられるまで空のまま表示されます。",
      confirmBulkDelete:      "一括削除の確認",
      confirmBulkDeleteText:  "本当に削除してもよろしいですか",
      logosWord:              "個のロゴ",
      selectedInUseWarning:   "個の選択されたロゴが使用中です。ロゴ要素はラベルに残りますが、新しいロゴが割り当てられるまで空のまま表示されます。",
      andMore:                "他",
      more:                   "個",
      deleteSelected:         "選択したものを削除",
      deleteAll:              "すべて削除",
      logosDeleted:           "削除しました",
      logoDeleted:            "ロゴを削除しました",
      logoDeletedInUse:       "ロゴを削除しました (ラベルは空のロゴスロットで保持されます)",
      logoRenamed:            "ロゴの名前を変更しました",
      logoUploaded:           "ロゴをアップロードしました",
      uploadFailed:           "アップロードに失敗しました",
      deleteFailed:           "削除に失敗しました",
      bulkDeleteFailed:       "一括削除に失敗しました",
      renameFailed:           "名前の変更に失敗しました",
      missing:                "見つかりません",
      skipped:                "スキップしました",
    },
    freetext: {
      sizes:      "フリーテキストサイズ",
      sizesHint:  "タブレットからフリーテキストを印刷する際にユーザーが選択できるラベルサイズ。1つをデフォルトとして指定します。",
      createSize: "サイズを作成",
    },
    dialogs: {
      preview: {
        title:             "プレビュー",
        hint:              "値を入力して、実際のデータでラベルがどう見えるか確認します。'サンプルデータを読み込む' でランダムな商品を読み込めます。",
        loadSample:        "サンプルデータを読み込む",
        sampleLoaded:      "サンプルを読み込みました ({name})",
        sampleFailed:      "サンプルデータを読み込めませんでした",
        noSampleAvailable: "サンプルとして使用できる有効な商品がありません",
        clearForm:         "フォームをクリア",
        apply:             "適用",
        noTokens:          "このラベルにはまだトークンがありません",
        pickDate:          "日付を選択",
        tabDate:           "日付",
        tabTime:           "時刻",
        now:               "現在",
        pickProduct:       "商品を選択",
        searchProduct:     "検索…",
        noProducts:        "有効な商品がありません",
        itemNotFound:      "商品が見つかりません",
        productListFailed: "商品リストを読み込めませんでした",
      },
      print: {
        title:        "ラベルを印刷",
        hint:         "ラベルを PDF または ZPL としてダウンロードします。",
        format:       "形式",
        dpi:          "解像度",
        noDataHint:   "サンプルデータが読み込まれていません。ラベルはプレースホルダーで表示されます。実際の値を読み込むにはプレビューを開いてください。",
        generateZpl:  "ZPL を生成",
        zplOutput:    "ZPL 出力",
        copyZpl:      "ZPL をコピー",
        downloadZpl:  ".zpl をダウンロード",
        copied:       "クリップボードにコピーしました",
        copyFailed:   "コピーできませんでした",
        pdfSuccess:   "PDF をダウンロードしました",
        failed:       "生成できませんでした",
      },
      logo: {
        title:             "ロゴをアップロード",
        selectFile:        "画像を選択",
        fileHint:          "PNG, JPG, GIF または WebP。最大 5 MB。",
        widthMm:           "幅 (mm)",
        widthHint:         "高さは自動的に計算されます",
        dpi:               "解像度",
        threshold:         "白黒しきい値",
        thresholdHint:     "低い = 白が多い。高い = 黒が多い。",
        original:          "オリジナル",
        mono:              "白黒",
        apply:             "適用",
      },
      logoPicker: {
        title:             "ロゴを選択",
        uploadNew:         "新規アップロード",
        search:            "ロゴを検索...",
        empty:             "ロゴはまだありません。最初の1つをアップロードして始めてください。",
        noMatch:           "検索に一致するロゴはありません",
      },
    },
    common: {
      unsaved:  "未保存の変更",
      cancel:   "キャンセル",
      close:    "閉じる",
      download: "ダウンロード",
      clear:    "クリア",
      refresh:  "更新",
      delete:   "削除",
      loading:  "読み込み中…",
      nutritionProfile:  "栄養プロファイル",
      nutritionProfiles: "栄養プロファイル",
    },
  },
}

export default designerI18n